/**
 * RESPONSIBILITY: the @Choice control drawn as native radios or checkboxes.
 *
 * `as: 'radio'` and `as: 'checkbox'` are presentation and nothing else — the value stored,
 * the schema and what validate() accepts are the same as the select and the chips this
 * replaces. A <select> hides its options behind a click, which is right for forty series
 * and wrong for the four things a visitor has to weigh up.
 *
 * The view is exported because a @List row needs the same set of inputs at cell size, and
 * two copies of "draw these options as inputs" is how the two drift apart.
 *
 * Registers with a tester + rank. The highest-ranked renderer for a schema node wins, so
 * adding a control is a registration — there is no switch on field type.
 */
import { rankWith, optionIs, type ControlProps } from '@jsonforms/core';
import { withJsonFormsControlProps, useJsonForms } from '@jsonforms/react';
import { resolveOptions } from '@framework/core';

export interface ChoiceGroupProps {
  options: readonly string[];
  /** Selected values. One entry at most when `multiple` is false. */
  held: readonly string[];
  multiple: boolean;
  /** Groups the radios, so two of these in one row do not fight over each other. */
  name: string;
  onToggle: (v: string) => void;
  /** Goes on the FIRST input, which is what a caption's htmlFor can point at. */
  id?: string;
}

export function ChoiceGroup({ options, held, multiple, name, onToggle, id }: ChoiceGroupProps) {
  return (
    <div className="ctl-optgroup" role={multiple ? 'group' : 'radiogroup'}>
      {options.map((v, i) => (
        <label key={v} className="ctl-opt">
          <input
            id={i === 0 ? id : undefined}
            type={multiple ? 'checkbox' : 'radio'}
            name={name}
            checked={held.includes(v)}
            // A radio cannot be un-picked by clicking it again, so onChange is enough;
            // the parent decides what toggling means for a set versus a single value.
            onChange={() => onToggle(v)}
          />
          <span>{v}</span>
        </label>
      ))}
    </div>
  );
}

function ChoiceGroupControlBase({ data, handleChange, path, label, uischema, errors }: ControlProps) {
  const opts = (uischema as { options?: Record<string, unknown> }).options ?? {};
  const multiple = Boolean(opts.multiple);

  // The whole document, because `in` resolves against the section root and a control
  // only ever knows its own value.
  const root = (useJsonForms().core?.data ?? {}) as Record<string, unknown>;
  const allowed = opts.in
    ? resolveOptions(String(opts.in), root)
    : ((opts.fixed as string[] | undefined) ?? []);

  const held: string[] = multiple
    ? (Array.isArray(data) ? (data as string[]) : [])
    : (data ? [String(data)] : []);

  const toggle = (v: string) =>
    handleChange(path, multiple
      ? (held.includes(v) ? held.filter((x) => x !== v) : [...held, v])
      : v);

  return (
    <div className="ctl">
      <label className="ctl-label">{label}</label>

      {allowed.length === 0 ? (
        <p className="ctl-hint">
          Nothing to choose from yet — add values to <code>{String(opts.in ?? '')}</code> first.
        </p>
      ) : (
        <ChoiceGroup
          options={allowed} held={held} multiple={multiple}
          name={path} onToggle={toggle}
        />
      )}

      {errors && <p className="ctl-error">{errors}</p>}
    </div>
  );
}

export const choiceGroupControl = {
  tester: rankWith(11, optionIs('widget', 'choicegroup')),
  renderer: withJsonFormsControlProps(ChoiceGroupControlBase),
};
